import React, { FC, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { Col, Grid, Row } from '../Grid';
import Collapsible from 'react-collapsible';
import {
    CONSUMPTION_MIX,
    ELECTRIC_CONSUMPTION,
    ELECTRIC_RANGE,
    ENGINE_POWER,
    FUEL,
    PROMOTED_SPECS_ORDER,
    STANDARD_FEATURES_FS_PREFIX,
    TECHNICAL_CHARACTERISTICS_FS_PREFIX,
} from '../../constants/characteristics';
import Icon, { Icons } from '../Icon';
import { CharacteristicsProps, ITechnicalSpecifications, ITechnicalSpecificationsData } from '.';
import UIDuck from '../../redux/commonDucks/ui.duck';

import { defaultBreakpoints, theme } from '../../styles/theme';
import useTranslations from '@hooks/useTranslations';
import { Link } from 'react-scroll';
import {
    getEnvironmentalAndTechData,
    getFilteredSortedTechSpecs,
    getPromotedTechnicalSpecifications,
    getSortedSpecs,
} from '@utils/Characteristics.utils';
import { useIsMyAccount } from '@hooks/useIsMyAccount';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useTechSpec } from '@hooks/useTechSpec';
import { getStockEnvironmentalData } from './helper/getStockEnviromentalData';
import StockEnvironmentalData from './helper/StockEnviromentalData';
import { isBrandOV, isMarketGB } from '../../constants/main';
import { getData } from '../CarOptionsInfoBox/helpers';
import { FEATURES_LIST, FeatureSwitchContext } from '../../context/featureSwitch';
import { TechSpecSection } from './TechSpecSection';
import { TechFeatureSection } from './TechFeatureSection';
import { HandledComponentError } from '@components/ErrorHandling';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { TriggerIcon } from '@components/Characteristics/TriggerIcon';
import TechPointsTemplate from '@components/CarOptionsInfoBox/TechPointsTemplate';
import { useRouter } from 'next/router';
import routes from '../../constants/routes';

const CharacteristicsTemplate: FC<CharacteristicsProps> = ({
    className,
    dispatch,
    characteristicsOpen: checkoutCharacteristicsOpen,
    carDetail,
    characteristicsRef,
    toggleOpen,
    isBasket,
    isConfigurator,
    isSummary,
}) => {
    const isMyAccountPage = useIsMyAccount();
    const characteristicsOpen = isMyAccountPage || checkoutCharacteristicsOpen;
    const router = useRouter();

    const [collapsibleVisibility, setCollabsibleTriger] = useState<boolean>(false);
    const { t } = useTranslations();
    const { getConsumptionLabel, getConsumptionText } = useTechSpec(carDetail);

    const excludedCategoriesStr = useContext(FeatureSwitchContext)?.FEATURE_SWITCH_EXCLUDED_CHARACTERISTICS_CATEGORIES ?? '';

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);

    useEffect(() => {
        const setCollabsibleTrigerByBreakpoints = () => {
            if (window.innerWidth > defaultBreakpoints.sm) {
                setCollabsibleTriger(true);
            } else {
                setCollabsibleTriger(false);
            }
        };

        setCollabsibleTrigerByBreakpoints();

        window.addEventListener('resize', setCollabsibleTrigerByBreakpoints);
        if (!isMyAccountPage) {
            document.getElementsByClassName(className)[0]?.scrollIntoView({ behavior: 'smooth' });
        }
        return () => window.removeEventListener('resize', setCollabsibleTrigerByBreakpoints);
    }, [className, isMyAccountPage]);

    const { engine, technicalSpecifications, standardFeatures, fuel, gearbox } = carDetail;

    const { environmentalData = null, techData = null } = engine?.specs
        ? getEnvironmentalAndTechData(engine.specs)
        : {};

    const technicalSpecs = technicalSpecifications && getPromotedTechnicalSpecifications(technicalSpecifications);

    const standardFeatureSpecifications = standardFeatures && getSortedSpecs(standardFeatures);

    const technicalSpecificationsFiltered =
        technicalSpecifications && getFilteredSortedTechSpecs(technicalSpecifications);

    const handleCloseCharacteristics = useCallback(() => {
        if (isMyAccountPage) {
            toggleOpen();
        } else {
            dispatch(UIDuck.closeCharacteristics());
        }
    }, [dispatch, isMyAccountPage, toggleOpen]);

    const { isStockJourney } = useCarStockJourneyCheck();
    const stockEnvironmentalData = getStockEnvironmentalData(carDetail);

    const stockFuel = carDetail.fuel;
    const stockGearbox = getData(gearbox.specs, 'type', 'label');

    if (!characteristicsOpen) return null;

    try {
        return characteristicsOpen ? (
            <Collapsible trigger="" open={characteristicsOpen}>
                <div
                    data-testid="TESTING_CHARACTERISTICS_GRID"
                    className={`${className} chars`}
                    ref={characteristicsRef || null}
                >
                    <Grid className="characteristicsGrid">
                        <Row>
                            <Col xs={12} md={12} className="characteristics-grid-col">
                                <h2 className="technicalTitle">{t('technicalFeatures.environmentalDetails.title')}</h2>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12} className="characteristics-col-title">
                                <span className="technicalSubtitle">
                                    <span>{engine.title}</span>
                                    <span>{gearbox.title}</span>
                                </span>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12} style={{ width: '100%' }}>
                                <div className="environmentalWrapper">
                                    <Col xs={12} sm={12} md={5}>
                                        <div
                                            className={`environmentalBlock ${
                                                isConfigurator || isBasket ? 'environmentalBlockBasket' : ''
                                            }`}
                                        >
                                            {techData &&
                                                !isStockJourney &&
                                                Object.keys(techData).map((key) => {
                                                    let element: ReactElement = <span>{fuel}</span>;
                                                    let message: string = t(
                                                        'technicalFeatures.environmentalDetails.engine.fuel'
                                                    );
                                                    let icon = Icons.Fuel;
                                                    if (key !== FUEL) {
                                                        element = (
                                                            <span>{getData(gearbox.specs, 'type', 'label')}</span>
                                                        );
                                                        message = t('technicalFeatures.environmentalDetails.gearbox');
                                                        icon = Icons.Manual;
                                                    }
                                                    return (
                                                        <div key={key} className="techBlock">
                                                            <div className={`imageWrapper imageWrapper-${key}`}>
                                                                <Icon
                                                                    name={icon}
                                                                    color={theme.colors.primary}
                                                                    height={25}
                                                                />
                                                            </div>
                                                            <div className="techWrapper">
                                                                <div className="techLabel">{message}</div>
                                                                {element}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                            {isStockJourney && (
                                                <>
                                                    <div className="techBlock">
                                                        <div className={'imageWrapper imageWrapper-fuel'}>
                                                            <Icon
                                                                name={Icons.Fuel}
                                                                color={theme.colors.primary}
                                                                height={25}
                                                            />
                                                        </div>
                                                        <div className="techWrapper">
                                                            <div className="techLabel">
                                                                {t(
                                                                    `technicalFeatures.environmentalDetails.engine.fuel`
                                                                )}
                                                            </div>
                                                            <span>{stockFuel}</span>
                                                        </div>
                                                    </div>
                                                    <div className="techBlock">
                                                        <div className={`imageWrapper imageWrapper-gearbox`}>
                                                            <Icon
                                                                name={Icons.Manual}
                                                                color={theme.colors.primary}
                                                                height={25}
                                                            />
                                                        </div>
                                                        <div className="techWrapper">
                                                            <div className="techLabel">
                                                                {t(`technicalFeatures.environmentalDetails.gearbox`)}
                                                            </div>
                                                            <span>{stockGearbox}</span>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </Col>
                                    <Col xs={12} md={7} sm={12}>
                                        <div
                                            className={`environmentalBlock ${
                                                isBasket ? 'environmentalBlockBasket' : ''
                                            }`}
                                        >
                                            {!isStockJourney &&
                                                environmentalData &&
                                                Object.keys(environmentalData).map((key: string) => {
                                                    const message: string = getConsumptionText(key);
                                                    const shouldShowUnit = ![
                                                        ELECTRIC_RANGE,
                                                        ELECTRIC_CONSUMPTION,
                                                        ENGINE_POWER,
                                                    ].includes(key);

                                                    return (
                                                        <div key={key} className="environmentalItem">
                                                            <span>{message}</span>
                                                            <div className="environmentalLabel">
                                                                {environmentalData[key]}
                                                                {shouldShowUnit && getConsumptionLabel(key)}
                                                            </div>
                                                            {key === CONSUMPTION_MIX && isSOLEnabled && (
                                                                <div className="environmentalLabel">
                                                                    {getConsumptionText(key)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            {isStockJourney && (
                                                <StockEnvironmentalData environmentalData={stockEnvironmentalData} />
                                            )}
                                        </div>
                                    </Col>
                                </div>
                            </Col>
                        </Row>
                        {router?.asPath?.includes(routes.SUMMARY) ||
                            router?.asPath?.includes(routes.BASKET) ||
                            (router?.asPath?.includes(routes.CAR) && (
                                <>
                                    <Row>
                                        <Col xs={12} md={12}>
                                            <span className="technicalDetails">
                                                {t('configurator.info.strongPoints')}
                                            </span>
                                        </Col>
                                    </Row>
                                    <div className="technicalDetails-mainBlock details">
                                        <Col className="tech" md={6} lg={7} sm={9} xs={7}>
                                            <div className="technicalWrapper">
                                                <Collapsible
                                                    open
                                                    trigger={
                                                        <div className="collapsibleTriggerHeader">
                                                            <h3 className="featureTitleWide">
                                                                {t(
                                                                    'technicalFeatures.environmentalDetails.specification'
                                                                )}
                                                            </h3>
                                                            {isBrandOV && !isMarketGB ? (
                                                                <TriggerIcon isExpanded />
                                                            ) : (
                                                                <Icon
                                                                    name={Icons.ChevronDown}
                                                                    color={theme.colors.primary}
                                                                    height={10}
                                                                />
                                                            )}
                                                        </div>
                                                    }
                                                    triggerDisabled={collapsibleVisibility}
                                                >
                                                    <TechPointsTemplate showBootSpace showPower={false} />
                                                </Collapsible>
                                            </div>
                                        </Col>
                                        <Col className="benefits" md={6} lg={4} sm={3} xs={5}>
                                            <div className="technicalWrapper">
                                                <Collapsible
                                                    open
                                                    trigger={
                                                        <div className="collapsibleTriggerHeader">
                                                            <h3 className="featureTitleWide">
                                                                {t('technicalFeatures.environmentalDetails.benefits')}
                                                            </h3>
                                                            {isBrandOV && !isMarketGB ? (
                                                                <TriggerIcon isExpanded />
                                                            ) : (
                                                                <Icon
                                                                    name={Icons.ChevronDown}
                                                                    color={theme.colors.primary}
                                                                    height={10}
                                                                />
                                                            )}
                                                        </div>
                                                    }
                                                    triggerDisabled={collapsibleVisibility}
                                                >
                                                    {!!(
                                                        carDetail.extraFields?.seriesEquipment?.length ||
                                                        carDetail?.seriesEquipment?.length
                                                    ) && (
                                                        <>
                                                            <div className="benefitItem">
                                                                <ul className="bullet-points">
                                                                    {(
                                                                        carDetail.extraFields?.seriesEquipment ??
                                                                        carDetail.seriesEquipment
                                                                    ).map((category: any) => {
                                                                        if (Array.isArray(category.data)) {
                                                                            return category.data.map(
                                                                                (equipment: any) => (
                                                                                    <li key={equipment.label}>
                                                                                        {equipment.label}
                                                                                    </li>
                                                                                )
                                                                            );
                                                                        } else if (category.data?.label) {
                                                                            return (
                                                                                <li key={category.data.label}>
                                                                                    {category.data.label}
                                                                                </li>
                                                                            );
                                                                        } else {
                                                                            return Object.keys(category.data).map(
                                                                                (key) => (
                                                                                    <li key={category.data[key].label}>
                                                                                        {category.data[key].label}
                                                                                    </li>
                                                                                )
                                                                            );
                                                                        }
                                                                    })}
                                                                </ul>
                                                            </div>
                                                        </>
                                                    )}
                                                </Collapsible>
                                            </div>
                                        </Col>
                                    </div>
                                </>
                            ))}
                        <Row>
                            <Col xs={12} md={12}>
                                <span className="technicalDetails">
                                    {t('technicalFeatures.environmentalDetails.details')}
                                </span>
                            </Col>
                        </Row>
                        <div className="technicalDetails-mainBlock" style={{ paddingBottom: '30px' }}>
                            {technicalSpecs &&
                                technicalSpecs.length &&
                                technicalSpecs.map((spec: ITechnicalSpecifications, index: number) => (
                                    <TechFeatureSection
                                        key={index}
                                        carDetail={carDetail}
                                        spec={spec}
                                        initialOpenState={collapsibleVisibility}
                                    />
                                ))}
                        </div>
                        <h2 className="technicalDetails characteristics">
                            {t('technicalFeatures.environmentalDetails.specification')}
                        </h2>
                        <div className="specificationBlock">
                            {Boolean(standardFeatureSpecifications && standardFeatureSpecifications.length) &&
                                standardFeatureSpecifications.map((spec: ITechnicalSpecifications, index: number) => {
                                    if (!excludedCategoriesStr?.includes(`${STANDARD_FEATURES_FS_PREFIX}:${spec.key}`)) {
                                        return <TechSpecSection key={index} spec={spec} initialOpenState={collapsibleVisibility}/>
                                    }
                                })}
                        </div>

                        <div className="specificationBlock">
                            {Boolean(technicalSpecificationsFiltered && technicalSpecificationsFiltered.length) &&
                                technicalSpecificationsFiltered.map((spec: ITechnicalSpecifications, index: number) => {
                                    const { category, key } = spec;
                                    if (excludedCategoriesStr?.includes(`${TECHNICAL_CHARACTERISTICS_FS_PREFIX}:${key}`)) {
                                        return null;
                                    }

                                    if (PROMOTED_SPECS_ORDER[key]) {
                                        return null;
                                    }

                                    return (
                                        Boolean(spec?.data?.length) && (
                                            <div key={index} className="technicalWrapper">
                                                <Collapsible
                                                    open={collapsibleVisibility}
                                                    trigger={<h3 className="featureTitle">{category}</h3>}
                                                    triggerDisabled={collapsibleVisibility}
                                                >
                                                    <ul className="featureList featureListWithValue">
                                                        {spec.data.map(
                                                            (data: ITechnicalSpecificationsData, i: number) => {
                                                                const { label, value } = data;
                                                                return (
                                                                    <li key={i}>
                                                                        <span
                                                                            className="featureInfo"
                                                                            dangerouslySetInnerHTML={{ __html: label }}
                                                                        />
                                                                        <span className="featureValue">{value}</span>
                                                                    </li>
                                                                );
                                                            }
                                                        )}
                                                    </ul>
                                                </Collapsible>
                                            </div>
                                        )
                                    );
                                })}
                        </div>
                        <Row className="hideTechnicalDetailWrapper">
                            <Col xs={12} className="hideTechnicalDetail">
                                {!isSummary && (
                                    <Link
                                        to="show-characteristics-button"
                                        offset={-300}
                                        smooth
                                        onClick={handleCloseCharacteristics}
                                    >
                                        {t('technicalFeatures.hideDetails')}
                                        {!isMarketGB && isBrandOV && (
                                            <>
                                                <br />
                                                <Icon name={Icons.ChevronUp} height={8} />
                                            </>
                                        )}
                                    </Link>
                                )}
                            </Col>
                        </Row>
                    </Grid>
                </div>
            </Collapsible>
        ) : null;
    } catch (e) {
        return <HandledComponentError error={e} />;
    }
};

export default CharacteristicsTemplate;
